import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { mapLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Facebook from "@ucs/Icons/Facebook";
import LinkedIn from "@ucs/Icons/LinkedIn";
import Twitter from "@ucs/Icons/Twitter";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    links = "links",
    direction = "direction",
    alignment = "alignment",
    opacity = "opacity",
    backgroundColor = "backgroundColor",
    hoverColor = "hoverColor",
    invertDirection = "invertDirection",
}

const enum icons {
    facebook = "Facebook",
    twitter = "Twitter",
    linkedin = "LinkedIn",
}

const enum alignment {
    left = "left",
    center = "center",
    right = "right",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.opacity]: number;
        [fields.backgroundColor]: string;
        [fields.hoverColor]: string;
        [fields.links]: LinkModel[];
        [fields.invertDirection]: boolean;
    };
}

interface LinkModel {
    fields: {
        title: string;
        icon: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        overriddenTitle: string;
    };
}

export interface LinkListStyles {
    linksHorizontalWrapper?: InjectableCss;
    linksVerticalWrapper?: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    linksHorizontalWrapper: {
        css: css`
            width: 100%;
            padding: 0 0;
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            & > * {
                &:not(:first-child) {
                    margin-left: 16px;
                }
            }
        `,
    },
    linksVerticalWrapper: {
        css: css`
            width: 100%;
            padding: 0;
            display: flex;
            flex-direction: column;
            & > * {
                &:not(:first-child) {
                    margin-top: 16px;
                }
            }
        `,
    },
};

export const socialLinksStyles = linkListStyles;

const styles = linkListStyles;

const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.facebook:
            return Facebook;
        case icons.twitter:
            return Twitter;
        case icons.linkedin:
            return LinkedIn;
        default:
            break;
    }

    return "";
};

const getAlignment = (align: string) => {
    switch (align) {
        case alignment.left:
            return "flex-start";
        case alignment.center:
            return "center";
        case alignment.right:
            return "flex-end";
        default:
            break;
    }
    return "";
};

interface OptionalFieldReturn {
    title: string;
    icon: string;
    openInNewWindow: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const links = mapLinks<LinkModel, OptionalFieldReturn>(state, ownProps.fields.links, (widgetLink, stateLink) => ({
        title: widgetLink.fields.title ?? stateLink?.title,
        icon: widgetLink.fields.icon,
        openInNewWindow: widgetLink.fields.openInNewWindow,
    }));
    return {
        links,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const NewsSocialLinks: FC<Props> = ({ fields, links }) => {
    const socialLinkListStyle = css`
        margin-top: 25px;
        ${({ theme }) => {
            return breakpointMediaQueries(theme, [
                css`
                    position: relative;
                    right: -120%;
                    top: -55px;
                    width: auto !important;
                    margin-top: 0;
                    height: 0;
                `,
            ], 'max');
        }}
        justify-content: ${getAlignment(fields.alignment)};
        align-items: ${getAlignment(fields.alignment)};
        ${fields.direction === "horizontal" ? styles.linksHorizontalWrapper?.css : styles.linksVerticalWrapper?.css}
        ${({ theme }) => {
            return breakpointMediaQueries(theme, [
                css`
                    ${fields.invertDirection && fields.direction === "horizontal"
                        ? styles.linksVerticalWrapper?.css
                        : styles.linksHorizontalWrapper?.css}
                    & > * {
                        &:not(:first-child) {
                            margin-top: 0;
                        }
                    }
                `,
            ]);
        }}
    `;

    const linkStyle = {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border-radius: 50px;
            background-color: ${fields.backgroundColor};
            opacity: ${fields.opacity / 100};

            &:hover {
                background-color: ${fields.hoverColor};
                color: ${UCSColors.gray10} !important;
            }
        `,
    };
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClickHandler = (url: any, target: any) => {
        if (!url) {
            return;
        }
        const updatedUrl = url+window.location.href;
        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }
            target ? window.open(updatedUrl, "_blank") : (window.location.href = updatedUrl);
        } else {
            history.push(updatedUrl);
        }
    };

    return (
        <StyledWrapper css={socialLinkListStyle}>
            {links.map((link, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper key={index}>
                    {link?.url && (
                        <Link
                            href=""
                            onClick={() => onClickHandler(link.url, link.openInNewWindow)}
                            target={link.openInNewWindow ? "_blank" : ""}
                            icon={{
                                iconProps: {
                                    color: `${UCSColors.gray10}`,
                                    src: GetIcon(link.icon),
                                    size: 20,
                                    css: css`
                                        margin-right: 0;
                                        &:hover {
                                            color: ${UCSColors.gray10} !important;
                                        }
                                    `,
                                },
                            }}
                            {...linkStyle}
                            hoverStyle={{ color: `${UCSColors.gray10} !important` }}
                        ></Link>
                    )}
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const contentTab = { displayName: "Content", sortOrder: 1 };

const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const definition: WidgetDefinition = {
    group: "Basic",
    icon: "Link",
    isSystem: true,
    fieldDefinitions: [
        {
            name: fields.links,
            editorTemplate: "ListField",
            tab: contentTab,
            getDisplay: (item: HasFields) => {
                return item.fields.title;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "icon",
                    displayName: "Select Icon",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Facebook", value: icons.facebook },
                        { displayName: "Twitter", value: icons.twitter },
                        { displayName: "LinkedIn", value: icons.linkedin },
                    ],
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: "destination",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Url",
                    },
                    isRequired: true,
                },
                {
                    name: "openInNewWindow",
                    editorTemplate: "CheckboxField",
                    defaultValue: true,
                },
            ],
        },
        {
            name: fields.direction,
            editorTemplate: "DropDownField",
            defaultValue: "horizontal",
            fieldType: "General",
            options: [
                {
                    displayName: "Vertical",
                    value: "vertical",
                },
                {
                    displayName: "Horizontal",
                    value: "horizontal",
                },
            ],
            hideEmptyOption: true,
            sortOrder: 0,
            tab: settingsTab,
        },
        {
            name: fields.alignment,
            editorTemplate: "DropDownField",
            defaultValue: alignment.left,
            fieldType: "General",
            options: [
                {
                    displayName: "Left",
                    value: alignment.left,
                },
                {
                    displayName: "Center",
                    value: alignment.center,
                },
                {
                    displayName: "Right",
                    value: alignment.right,
                },
            ],
            hideEmptyOption: true,
            tab: settingsTab,
            sortOrder: 1,
        },
        {
            name: fields.opacity,
            displayName: "Opacity",
            editorTemplate: "IntegerField",
            defaultValue: 100,
            min: 1,
            max: 100,
            fieldType: "General",
            sortOrder: 2,
            tab: settingsTab,
        },
        {
            name: fields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: `${UCSColors.green00}`,
            fieldType: "General",
            sortOrder: 3,
            tab: settingsTab,
        },
        {
            name: fields.hoverColor,
            displayName: "Hover Color",
            editorTemplate: "ColorPickerField",
            defaultValue: `${UCSColors.gray00}`,
            fieldType: "General",
            sortOrder: 4,
            tab: settingsTab,
        },
        {
            name: fields.invertDirection,
            displayName: "Invert Direction on Mobile",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            sortOrder: 5,
            tab: settingsTab,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(NewsSocialLinks),
    definition,
};

export default widgetModule;
